import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

const slideDown = keyframes`
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const StyledHeader = styled.header`
  background: linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%);
  color: white;
  padding: 1rem 0;
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  animation: ${slideDown} 0.5s ease;
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255,255,255,0.1);
`;

const Nav = styled.nav`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 2rem;

  @media (max-width: 768px) {
    padding: 0 1rem;
    gap: 0.75rem;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const LogoImg = styled.img`
  width: 50px;
  height: 50px;
  background: white;
  border-radius: 50%;
  padding: 5px;
  transition: transform 0.3s;
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);

  ${Logo}:hover & {
    transform: rotate(5deg) scale(1.05);
  }
`;

const LogoText = styled.div`
  display: flex;
  flex-direction: column;
`;

const LogoMain = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
`;

const LogoSubtitle = styled.div`
  font-size: 0.7rem;
  font-weight: 300;
  opacity: 0.7;
  letter-spacing: 0.5px;
`;

const NavLinks = styled.ul<{ $isOpen: boolean }>`
  display: flex;
  gap: 2rem;
  list-style: none;
  margin: 0;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: rgba(46, 125, 50, 0.95);
    padding: 1rem;
    transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-100%)'};
    opacity: ${props => props.$isOpen ? '1' : '0'};
    transition: transform 0.3s ease, opacity 0.3s ease;
    box-shadow: 0 5px 10px rgba(0,0,0,0.2);
  }
`;

const NavLink = styled.a`
  color: white;
  text-decoration: none;
  transition: opacity 0.3s, transform 0.3s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;

  &:hover {
    opacity: 0.8;
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 0.5rem 0;
    border-bottom: 1px solid rgba(255,255,255,0.1);
  }
`;

const Hamburger = styled.button`
  display: none;
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;

  @media (max-width: 768px) {
    display: block;
  }
`;

const Header: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleMenu = () => setIsOpen(!isOpen);

  return (
    <StyledHeader>
      <Nav>
        <Logo>
          <LogoImg src="/logo.jpeg" alt="Safe-Revive Africa Logo" />
          <LogoText>
            <LogoMain>Safe-Revive Africa</LogoMain>
            <LogoSubtitle>Transforming Communities</LogoSubtitle>
          </LogoText>
        </Logo>
        <Hamburger onClick={toggleMenu}>
          {isOpen ? '✕' : '☰'}
        </Hamburger>
        <NavLinks $isOpen={isOpen}>
          <li><NavLink href="#home">🏠 Home</NavLink></li>
          <li><NavLink href="#about">ℹ️ About</NavLink></li>
          <li><NavLink href="#pillars">🏗️ Our Pillars</NavLink></li>
          <li><NavLink href="#impact">📊 Field Activities</NavLink></li>
          <li><NavLink href="#research">🔬 Press</NavLink></li>
          <li><NavLink href="#contact">📞 Contact</NavLink></li>
        </NavLinks>
      </Nav>
    </StyledHeader>
  );
};

export default Header;